import { cloneElement } from "react";

import type { useCheckoutSessionConfig } from "@dues/hooks/useCheckoutSessionConfig";
import {
	identityUpdateContactSendCode,
	identityUpdateContactVerifyCode,
} from "@libs/acp/apis/updateAccount/updateAccountAPI";
import { CheckoutGroup } from "@libs/renegkit";
import { maskCellphone } from "@libs/utils/masks/cellphone";

import useAuthenticatedUser from "@libs/acp/hooks/useAuthenticatedUser";

type CheckoutRoutesProps = {
	checkoutSessionConfig: ReturnType<typeof useCheckoutSessionConfig>;
};

export function CheckoutRoutes({ checkoutSessionConfig }: CheckoutRoutesProps) {
	const { authenticatedUser, addAuthenticatedUser } = useAuthenticatedUser();
	const customer = authenticatedUser?.customer;

	const checkoutContactConfig = {
		isNCADCheckout: customer?.required_updates?.includes("account_ncad"),
		isNeverEngaged: false,
		userEmail: customer?.email ?? "",
		userPhone: maskCellphone(customer?.phone ?? ""),
		onSendCode: async (payload: {
			type: "celular" | "e-mail";
			contact: string;
		}) => {
			const contactType = payload.type === "celular" ? "phone" : "email";
			return identityUpdateContactSendCode({
				type: contactType,
				contact: payload.contact,
			});
		},
		onVerifyCode: async (payload: {
			type: "celular" | "e-mail";
			contact: string;
			token: string;
		}) => {
			const isPhoneFlow = payload.type === "celular";
			const contactType = isPhoneFlow ? "phone" : "email";
			await identityUpdateContactVerifyCode({
				type: contactType,
				token: payload.token,
			});
			addAuthenticatedUser(
				{
					...authenticatedUser,
					customer: {
						...customer,
						...(isPhoneFlow
							? { phone: payload.contact }
							: { email: payload.contact }),
					},
				},
				"acp",
			);
		},
	};

	return CheckoutGroup({
		contactConfig: checkoutContactConfig,
		sessionConfig: checkoutSessionConfig,
	}).map((route) => cloneElement(route, { key: route.props.path }));
}
